// Splits message text into plain and link parts, so links can be rendered as
// anchors without putting raw client text into v-html
const LINK_PATTERN =
  /(https?:\/\/[^\s]+[^\s.,:;"')\]}]|www\.[^\s]+[^\s.,:;"')\]}])/gi

const isLink = part => /^(https?:\/\/|www\.)/i.test(part)

export const splitTextLinks = text =>
  String(text)
    .split(LINK_PATTERN)
    .filter(part => part !== '')
    .map(part => ({
      text: part,
      href: isLink(part) ? part.replace(/^www\./i, 'https://www.') : null,
    }))

// The answering bot writes in markdown; we render the little of it that it uses
// as parts, so its text never has to reach v-html either
const MARKDOWN_LINK = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g
const BOLD = /\*\*([^*]+)\*\*/g

// split() with capture groups yields [plain, label, href, plain, ...]
const splitMarkdownLinks = (chunk, bold) => {
  const pieces = chunk.split(MARKDOWN_LINK)
  const parts = []
  for (let index = 0; index < pieces.length; index += 3) {
    parts.push(
      ...splitTextLinks(pieces[index]).map(part => ({ ...part, bold }))
    )
    if (pieces[index + 1] !== undefined)
      parts.push({ text: pieces[index + 1], href: pieces[index + 2], bold })
  }
  return parts
}

// Messengers that render html get her answer in html, so the operator sees the
// same thread as the client: tags off, links and bold kept
const HTML_LINK = /<a\s+href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/g
const HTML_BOLD = /<(?:b|strong)>([\s\S]*?)<\/(?:b|strong)>/g
const HTML_TAG = /<[^>]+>/g
const ENTITIES = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
}

const plainly = text =>
  text
    .replace(HTML_TAG, '')
    .replace(/&(amp|lt|gt|quot|#39);/g, entity => ENTITIES[entity])

const splitHtmlLinks = (chunk, bold) => {
  const pieces = chunk.split(HTML_LINK)
  const parts = []
  for (let index = 0; index < pieces.length; index += 3) {
    parts.push({ text: plainly(pieces[index]), href: null, bold })
    if (pieces[index + 1] !== undefined)
      parts.push({
        text: plainly(pieces[index + 2]),
        href: pieces[index + 1],
        bold,
      })
  }
  return parts
}

export const splitHtmlText = text =>
  String(text)
    .split(HTML_BOLD)
    .flatMap((chunk, index) => splitHtmlLinks(chunk, index % 2 === 1))
    .filter(part => part.text !== '')

export const looksLikeHtml = text =>
  /<(a|b|strong|i|em|br|code|pre)\b/i.test(String(text))

export const splitBotText = text =>
  looksLikeHtml(text)
    ? splitHtmlText(text)
    : String(text)
        .split(BOLD)
        .flatMap((chunk, index) => splitMarkdownLinks(chunk, index % 2 === 1))
        .filter(part => part.text !== '')

// One line in the list: the bot's markers only get in the way there
export const plainBotText = text =>
  splitBotText(text)
    .map(part => part.text)
    .join('')
